import { useCallback, useEffect, useRef, useState } from 'react'
import { MarkupShape, styleOf } from './MarkupShape'
import { DRAW_TOOLS } from './useMarkupTools'
import {
  eventToNorm,
  isBoxType,
  LINE_TYPES,
  translateGeometry,
  resizeBox,
  resizeLine,
  defaultStyle,
  geometryFromDraw,
  isDrawable,
  clampBoxToPage,
  dist,
} from './markupGeometry'
import './MarkupLayer.css'

const TEXTBOX_SIZE = { w: 0.3, h: 0.08 }

function MarkupTextbox({ markup, selected, editing, interactive, onPointerDown, onEdit, onChangeText }) {
  const g = markup.geometry || {}
  const { color, opacity, fontSize } = styleOf(markup)
  const [draft, setDraft] = useState(markup.text || '')

  useEffect(() => {
    setDraft(markup.text || '')
  }, [markup.text])

  return (
    <div
      className={'markup-textbox' + (selected ? ' is-selected' : '')}
      data-markup-id={markup._id}
      style={{
        left: `${(g.x || 0) * 100}%`,
        top: `${(g.y || 0) * 100}%`,
        width: `${(g.w || 0) * 100}%`,
        height: `${(g.h || 0) * 100}%`,
        color,
        opacity,
        fontSize,
        pointerEvents: interactive ? 'auto' : 'none',
      }}
      onPointerDown={(e) => {
        if (!interactive) return
        e.stopPropagation()
        if (e.target?.tagName === 'TEXTAREA') return
        onPointerDown(e)
      }}
      onDoubleClick={(e) => {
        if (!interactive) return
        e.stopPropagation()
        onEdit?.(markup._id)
      }}
    >
      {editing ? (
        <textarea
          className="markup-textbox-input"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onPointerDown={(e) => e.stopPropagation()}
          onBlur={() => onChangeText?.(markup._id, draft)}
        />
      ) : (
        markup.text || ''
      )}
      {selected && interactive ? (
        <>
          <span className="markup-tb-handle" data-handle="nw" style={{ left: -4, top: -4 }} />
          <span className="markup-tb-handle" data-handle="ne" style={{ right: -4, top: -4 }} />
          <span className="markup-tb-handle" data-handle="sw" style={{ left: -4, bottom: -4 }} />
          <span className="markup-tb-handle" data-handle="se" style={{ right: -4, bottom: -4 }} />
        </>
      ) : null}
    </div>
  )
}

export function MarkupLayer({
  pageNumber,
  markups = [],
  rotation = 0,
  tool = 'select',
  color = '#facc15',
  selectedId,
  onSelect,
  onCreate,
  onUpdate,
}) {
  const wrapRef = useRef(null)
  const rotRef = useRef(null)
  const dragRef = useRef(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  const [draft, setDraft] = useState(null)
  const [live, setLive] = useState(null)
  const [editId, setEditId] = useState(null)

  const interactive = rotation === 0
  const drawing = interactive && DRAW_TOOLS.includes(tool)

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return undefined
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setSize({ w: width, h: height })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      dragRef.current = null
      setDraft(null)
      setLive(null)
      setEditId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const swapped = rotation === 90 || rotation === 270
  const innerW = swapped ? size.h : size.w
  const innerH = swapped ? size.w : size.h

  const displayed = markups.map((m) =>
    live && m._id === live.id ? { ...m, geometry: live.geometry } : m
  )
  const shapes = displayed.filter((m) => m.type !== 'textbox')
  const boxes = displayed.filter((m) => m.type === 'textbox')

  const normFromEvent = useCallback((e) => {
    const el = rotRef.current
    if (!el) return { x: 0, y: 0 }
    return eventToNorm(e, el)
  }, [])

  const beginCapture = (e) => {
    rotRef.current?.setPointerCapture?.(e.pointerId)
  }

  const handlePointerDown = (e) => {
    if (!interactive) return
    if (e.button != null && e.button !== 0) return
    const handle = e.target?.dataset?.handle
    const markupId = e.target?.closest?.('[data-markup-id]')?.dataset?.markupId
    const pt = normFromEvent(e)

    if (drawing) {
      e.preventDefault()
      beginCapture(e)
      onSelect?.(null)
      setEditId(null)
      if (tool === 'textbox') {
        const geometry = clampBoxToPage({ x: pt.x, y: pt.y, ...TEXTBOX_SIZE })
        Promise.resolve(
          onCreate?.({
            type: 'textbox',
            page: pageNumber,
            geometry,
            style: defaultStyle('textbox', color),
            text: '',
          })
        ).then((created) => {
          if (created?._id) {
            onSelect?.(created._id)
            setEditId(created._id)
          }
        })
        return
      }
      dragRef.current = { kind: 'draw', start: pt, points: [pt] }
      setDraft({
        _id: 'draft',
        type: tool,
        geometry: geometryFromDraw(tool, pt, pt, [pt]),
        style: defaultStyle(tool, color),
      })
      return
    }

    if (handle && markupId) {
      const markup = displayed.find((m) => m._id === markupId)
      if (!markup) return
      e.stopPropagation()
      beginCapture(e)
      onSelect?.(markupId)
      setEditId(null)
      dragRef.current = { kind: 'resize', id: markupId, handle, origin: markup.geometry }
      return
    }

    if (markupId) {
      if (editId === markupId) return
      const markup = displayed.find((m) => m._id === markupId)
      if (!markup) return
      e.stopPropagation()
      beginCapture(e)
      onSelect?.(markupId)
      dragRef.current = {
        kind: 'move',
        id: markupId,
        start: pt,
        origin: markup.geometry,
      }
      return
    }

    onSelect?.(null)
    setEditId(null)
  }

  const handlePointerMove = (e) => {
    const drag = dragRef.current
    if (!drag) return
    const pt = normFromEvent(e)
    if (drag.kind === 'draw') {
      const points =
        tool === 'ink' && dist(drag.points[drag.points.length - 1], pt) > 0.003
          ? [...drag.points, pt]
          : drag.points
      if (tool === 'ink') drag.points = points
      setDraft({
        _id: 'draft',
        type: tool,
        geometry: geometryFromDraw(tool, drag.start, pt, points),
        style: defaultStyle(tool, color),
      })
      return
    }
    if (drag.kind === 'move') {
      const markup = markups.find((m) => m._id === drag.id)
      if (!markup) return
      const geometry = translateGeometry(markup.type, drag.origin, pt.x - drag.start.x, pt.y - drag.start.y)
      drag.geometry = geometry
      setLive({ id: drag.id, geometry })
      return
    }
    if (drag.kind === 'resize') {
      const markup = markups.find((m) => m._id === drag.id)
      if (!markup) return
      const geometry = isBoxType(markup.type)
        ? resizeBox(drag.origin, drag.handle, pt)
        : LINE_TYPES.includes(markup.type)
          ? resizeLine(drag.origin, drag.handle, pt)
          : drag.origin
      drag.geometry = geometry
      setLive({ id: drag.id, geometry })
    }
  }

  const handlePointerUp = (e) => {
    const drag = dragRef.current
    dragRef.current = null
    if (rotRef.current?.hasPointerCapture?.(e.pointerId)) {
      rotRef.current.releasePointerCapture(e.pointerId)
    }
    if (!drag) return

    if (drag.kind === 'draw') {
      const pt = normFromEvent(e)
      const points = tool === 'ink' ? drag.points : undefined
      const geometry = geometryFromDraw(tool, drag.start, pt, points)
      setDraft(null)
      if (!isDrawable(tool, geometry)) return
      onCreate?.({
        type: tool,
        page: pageNumber,
        geometry,
        style: defaultStyle(tool, color),
        text: '',
      })
      return
    }

    if ((drag.kind === 'move' || drag.kind === 'resize') && drag.geometry) {
      onUpdate?.(drag.id, { geometry: drag.geometry })
      setLive(null)
    }
  }

  return (
    <div
      className={
        'markup-layer' +
        (drawing ? ' is-drawing' : '') +
        (interactive && !drawing ? ' is-selecting' : '')
      }
      ref={wrapRef}
    >
      {size.w > 0 ? (
        <div
          className="markup-rot"
          ref={rotRef}
          style={{
            width: innerW,
            height: innerH,
            transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
            touchAction: drawing ? 'none' : undefined,
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <svg className="markup-svg" width={innerW} height={innerH}>
            {shapes.map((markup) => (
              <MarkupShape
                key={markup._id}
                markup={markup}
                pageW={innerW}
                pageH={innerH}
                selected={markup._id === selectedId}
                showHandles={interactive && tool === 'select' && markup._id === selectedId}
              />
            ))}
            {draft && draft.type !== 'textbox' ? (
              <MarkupShape markup={draft} pageW={innerW} pageH={innerH} selected={false} showHandles={false} />
            ) : null}
          </svg>
          {boxes.map((markup) => (
            <MarkupTextbox
              key={markup._id}
              markup={markup}
              selected={markup._id === selectedId}
              editing={editId === markup._id}
              interactive={interactive && !drawing}
              onPointerDown={handlePointerDown}
              onEdit={(id) => {
                onSelect?.(id)
                setEditId(id)
              }}
              onChangeText={(id, text) => {
                setEditId(null)
                onUpdate?.(id, { text })
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
